import { useState } from 'react'
import type {
  BlueskyCredentials,
  InstagramCredentials,
  RedditBrowserCredentials,
  RedditCredentials,
  TargetPlatform,
  ThreadsCredentials,
  XCredentials,
} from '@/api/client'
import { Field, Input } from '@/ui'

/** What the credential form holds, for any of the platforms. */
export interface CredDraft {
  threadsToken: string
  redditClientId: string
  redditClientSecret: string
  redditRefreshToken: string
  redditUserAgent: string
  bsURL: string
  bsKey: string
  bsJar: string
  bsAccount: string
  xApiKey: string
  xApiSecret: string
  xAccessToken: string
  xAccessSecret: string
  igUserId: string
  igToken: string
  /** yyyy-mm-dd; optional. */
  igExpires: string
  bskyHandle: string
  bskyPassword: string
  bskyPds: string
}

export const emptyCred = (): CredDraft => ({
  threadsToken: '',
  redditClientId: '',
  redditClientSecret: '',
  redditRefreshToken: '',
  redditUserAgent: '',
  bsURL: 'https://browser.pentarab.com',
  bsKey: '',
  bsJar: 'reddit',
  bsAccount: '',
  xApiKey: '',
  xApiSecret: '',
  xAccessToken: '',
  xAccessSecret: '',
  igUserId: '',
  igToken: '',
  igExpires: '',
  bskyHandle: '',
  bskyPassword: '',
  bskyPds: '',
})

type Creds =
  | ThreadsCredentials
  | RedditCredentials
  | RedditBrowserCredentials
  | XCredentials
  | InstagramCredentials
  | BlueskyCredentials

export function buildCredentials(platform: TargetPlatform, d: CredDraft): Creds {
  if (platform === 'threads') return { token: d.threadsToken }
  if (platform === 'instagram') {
    return {
      ig_user_id: d.igUserId.trim(),
      access_token: d.igToken.trim(),
      ...(d.igExpires ? { token_expires_at: `${d.igExpires}T00:00:00Z` } : {}),
    }
  }
  if (platform === 'bluesky') {
    return {
      handle: d.bskyHandle.trim().replace(/^@/, ''),
      app_password: d.bskyPassword.trim(),
      ...(d.bskyPds.trim() ? { pds_url: d.bskyPds.trim() } : {}),
    }
  }
  if (platform === 'x') {
    return {
      api_key: d.xApiKey.trim(),
      api_secret: d.xApiSecret.trim(),
      access_token: d.xAccessToken.trim(),
      access_secret: d.xAccessSecret.trim(),
    }
  }
  if (platform === 'reddit_browser') {
    return {
      browser_url: d.bsURL,
      api_key: d.bsKey,
      cookie_jar: d.bsJar,
      account: d.bsAccount.replace(/^u\//, ''),
    }
  }
  return {
    client_id: d.redditClientId,
    client_secret: d.redditClientSecret,
    refresh_token: d.redditRefreshToken,
    user_agent: d.redditUserAgent,
  }
}

const filled = (...v: string[]) => v.every((s) => s.trim().length > 0)

/** Every field the platform needs is filled in. */
export function credentialsComplete(platform: TargetPlatform, d: CredDraft): boolean {
  if (platform === 'threads') return filled(d.threadsToken)
  if (platform === 'instagram') return filled(d.igUserId, d.igToken)
  if (platform === 'bluesky') return filled(d.bskyHandle, d.bskyPassword)
  if (platform === 'x') return filled(d.xApiKey, d.xApiSecret, d.xAccessToken, d.xAccessSecret)
  if (platform === 'reddit_browser') return filled(d.bsURL, d.bsKey, d.bsJar, d.bsAccount)
  return filled(d.redditClientId, d.redditClientSecret, d.redditRefreshToken, d.redditUserAgent)
}

/** The user typed something in a field the platform needs (the browser URL and
 *  cookie jar start with defaults and do not count). */
export function credentialsTouched(platform: TargetPlatform, d: CredDraft): boolean {
  if (platform === 'threads') return d.threadsToken !== ''
  if (platform === 'instagram') return d.igUserId !== '' || d.igToken !== '' || d.igExpires !== ''
  if (platform === 'bluesky') return d.bskyHandle !== '' || d.bskyPassword !== ''
  if (platform === 'x') return d.xApiKey !== '' || d.xApiSecret !== '' || d.xAccessToken !== '' || d.xAccessSecret !== ''
  if (platform === 'reddit_browser') return d.bsKey !== '' || d.bsAccount !== ''
  return (
    d.redditClientId !== '' || d.redditClientSecret !== '' || d.redditRefreshToken !== '' || d.redditUserAgent !== ''
  )
}

function SecretInput({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  const [shown, setShown] = useState(false)
  return (
    <div className="relative">
      <Input
        type={shown ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pr-14"
        placeholder={placeholder}
        autoComplete="new-password"
      />
      <button
        type="button"
        onClick={() => setShown((v) => !v)}
        className="absolute inset-y-0 right-0 px-3 text-meta text-muted-foreground hover:text-foreground"
      >
        {shown ? '숨기기' : '보기'}
      </button>
    </div>
  )
}

/** The credential fields of one platform. The secrets stay on the server and
 *  never come back, so this form only ever holds what is typed now. */
export function CredentialFields({
  platform,
  value,
  onChange,
}: {
  platform: TargetPlatform
  value: CredDraft
  onChange: (v: CredDraft) => void
}) {
  const set = <K extends keyof CredDraft>(k: K, v: CredDraft[K]) => onChange({ ...value, [k]: v })

  if (platform === 'instagram') {
    return (
      <div className="space-y-3">
        <p className="text-meta text-muted-foreground">
          우리 쪽 Instagram 프로 계정(비즈니스·크리에이터)이다. 다른 사람의 공개 프로필을 불러올 때만 쓰고, 이 계정으로 DM을 보내지는 않는다.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="ig_user_id">
            <Input value={value.igUserId} onChange={(e) => set('igUserId', e.target.value)} placeholder="1784…" inputMode="numeric" />
          </Field>
          <Field label="access_token">
            <SecretInput value={value.igToken} onChange={(v) => set('igToken', v)} placeholder="Instagram access token" />
          </Field>
          <Field label="토큰 만료일 (선택)" hint="적어 두면 만료 전에 알아볼 수 있다.">
            <Input type="date" value={value.igExpires} onChange={(e) => set('igExpires', e.target.value)} />
          </Field>
        </div>
      </div>
    )
  }
  if (platform === 'bluesky') {
    return (
      <div className="space-y-3">
        <p className="text-meta text-muted-foreground">
          DM을 보내는 Bluesky 계정이다. 설정 &gt; 앱 비밀번호에서 DM 접근을 허용한 앱 비밀번호를 만든다. 주소는 비워 두면 https://bsky.social을 쓴다.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="핸들">
            <Input value={value.bskyHandle} onChange={(e) => set('bskyHandle', e.target.value)} placeholder="name.bsky.social" />
          </Field>
          <Field label="앱 비밀번호">
            <SecretInput value={value.bskyPassword} onChange={(v) => set('bskyPassword', v)} placeholder="xxxx-xxxx-xxxx-xxxx" />
          </Field>
          <Field label="PDS 주소 (선택)">
            <Input value={value.bskyPds} onChange={(e) => set('bskyPds', e.target.value)} placeholder="https://bsky.social" />
          </Field>
        </div>
      </div>
    )
  }
  if (platform === 'reddit_browser') {
    return (
      <div className="space-y-3">
        <p className="text-meta text-muted-foreground">
          browser-saas에 저장된 로그인 세션으로 글쓰기 화면을 직접 조작해 올린다. 이미지를 붙일 수 있는 방법은 이것뿐이다.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="browser-saas 주소">
            <Input value={value.bsURL} onChange={(e) => set('bsURL', e.target.value)} />
          </Field>
          <Field label="API 키">
            <SecretInput value={value.bsKey} onChange={(v) => set('bsKey', v)} placeholder="browser-saas X-API-Key" />
          </Field>
          <Field label="쿠키 저장소 이름">
            <Input value={value.bsJar} onChange={(e) => set('bsJar', e.target.value)} placeholder="reddit" />
          </Field>
          <Field label="Reddit 계정">
            <Input value={value.bsAccount} onChange={(e) => set('bsAccount', e.target.value)} placeholder="robostoa" />
          </Field>
        </div>
      </div>
    )
  }
  if (platform === 'x') {
    return (
      <div className="space-y-3">
        <p className="text-meta text-muted-foreground">
          X 개발자 콘솔의 앱 &gt; Keys and tokens에서 받는다. 앱 권한을 Read and write로 바꾼 뒤에 Access Token을 만들어야 글을 올릴
          수 있다. 네 값 모두 만료되지 않는다. 요청마다 크레딧이 빠진다 — 글 $0.015, 링크가 든 글 $0.20, 반응 읽기 $0.005.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="API Key">
            <SecretInput value={value.xApiKey} onChange={(v) => set('xApiKey', v)} placeholder="Consumer Key" />
          </Field>
          <Field label="API Key Secret">
            <SecretInput value={value.xApiSecret} onChange={(v) => set('xApiSecret', v)} placeholder="Consumer Secret" />
          </Field>
          <Field label="Access Token">
            <SecretInput value={value.xAccessToken} onChange={(v) => set('xAccessToken', v)} placeholder="1234567890-…" />
          </Field>
          <Field label="Access Token Secret">
            <SecretInput value={value.xAccessSecret} onChange={(v) => set('xAccessSecret', v)} />
          </Field>
        </div>
      </div>
    )
  }
  if (platform === 'threads') {
    return (
      <Field label="액세스 토큰">
        <SecretInput
          value={value.threadsToken}
          onChange={(v) => set('threadsToken', v)}
          placeholder="Threads API access token"
        />
      </Field>
    )
  }
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Client ID">
          <Input
            value={value.redditClientId}
            onChange={(e) => set('redditClientId', e.target.value)}
            placeholder="Reddit app client_id"
          />
        </Field>
        <Field label="Client Secret">
          <SecretInput
            value={value.redditClientSecret}
            onChange={(v) => set('redditClientSecret', v)}
            placeholder="Reddit app client_secret"
          />
        </Field>
      </div>
      <Field label="Refresh Token">
        <SecretInput
          value={value.redditRefreshToken}
          onChange={(v) => set('redditRefreshToken', v)}
          placeholder="OAuth refresh_token"
        />
      </Field>
      <Field label="User-Agent">
        <Input
          value={value.redditUserAgent}
          onChange={(e) => set('redditUserAgent', e.target.value)}
          placeholder="MyApp/1.0 by u/myuser"
        />
      </Field>
    </div>
  )
}
